/// <amd-module name="typeahead/core/plugins/PrefetchLoader" />
/// <amd-dependency path="typeahead/core/context" name="coreContext"/>

/// <reference path="../../../typings/typeahead/plugins.d.ts" />
/// <reference path="../../../typings/thirdparty/jquery/jquery.d.ts" />

import {IFlightOriginSuggestion} from "typeahead/core/FlightOriginSuggestion";
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

export interface IActiveSearch {
    [lob: string]: boolean;
}

export interface  IPrefetchBucketAssignments {
    [expID: string]: number;
}

export interface IPrefetchData {
    activeSearch?: IActiveSearch;
    carRentalHints?: string[];
    experiments?: IPrefetchBucketAssignments;
    flightOriginSuggestion?: IFlightOriginSuggestion;
}

export class PrefetchLoader {
    public static getInstance(): PrefetchLoader {
        if (!PrefetchLoader.instance) {
            PrefetchLoader.instance = new PrefetchLoader();
        }
        return PrefetchLoader.instance;
    }

    private static instance: PrefetchLoader;

    private hintsDeferred: JQueryDeferred<string[]> = $.Deferred();
    private experimentsDeferred: JQueryDeferred<IPrefetchBucketAssignments> = $.Deferred();
    private activeSearchDeferred: JQueryDeferred<IActiveSearch> = $.Deferred();
    private flightOriginSuggestion: JQueryDeferred<IFlightOriginSuggestion> = $.Deferred();

    private constructor() {}

    public prefetch(abIDs: string): JQueryPromise<IPrefetchData> {
        let def: JQueryDeferred<IPrefetchData> = $.Deferred();
        let context: IContext = {
            clientId: TypeaheadUtils.getClientId(),
            deviceType: TypeaheadUtils.getDeviceType(),
            expuserid: TypeaheadUtils.getExpUserId(),
            getShortPackageType: (): string => { return undefined; },
            guid: TypeaheadUtils.getGuid(),
            lob: undefined, // lob is unknown during prefetch call
            locale: coreContext.locale,
            serviceUrl: coreContext.serviceUrl,
            siteid: coreContext.siteid,
        };

        let prefetchUrl: string = context.serviceUrl + "/api/v4/prefetch/?callback=?&format=jsonp"
            + "&siteid=" + context.siteid
            + "&locale=" + context.locale
            + "&guid=" + context.guid
            + "&expuserid=" + context.expuserid
            + "&evalExp=" + abIDs;

        let latLong: string = TypeaheadUtils.getUserLocation();
        if (latLong) {
            prefetchUrl += "&loc=" + latLong;
        }
        this.makePrefetchRequest(prefetchUrl).done((data: IPrefetchData) => {
            this.populatePrefetchData(data);
        }).always((data: IPrefetchData) => def.resolve(data));

        return def.promise();
    }

    public getPrefetchExperiments(): JQueryPromise<IPrefetchBucketAssignments> {
        return this.experimentsDeferred.promise();
    }

    public getPrefetchCarRentalHints(): JQueryPromise<string[]> {
        return this.hintsDeferred.promise();
    }

    public getPrefetchFlightOriginSuggestion(): JQueryPromise<IFlightOriginSuggestion> {
        return this.flightOriginSuggestion.promise();
    }

    /**
     * For active search make sure there is no wait introduced in prefetch() due to expuserid
     * We need expuserid to ensure consistent cross device experience for user
     * @returns {JQueryPromise<IActiveSearch>}
     */
    public getPrefetchActiveSearch(): JQueryPromise<IActiveSearch> {
        return this.activeSearchDeferred.promise();
    }

    private makePrefetchRequest(prefetchUrl: string): JQueryPromise<IPrefetchData> {
        let deferred: JQueryDeferred<IPrefetchData> = $.Deferred();
        let emptyPrefetchData: IPrefetchData = {};
        $.getJSON(prefetchUrl)
            .done((data: IPrefetchData) => {
                deferred.resolve(data);
            })
            .fail(() => {
                deferred.reject(emptyPrefetchData);
            });

        return deferred.promise();
    }

    private populatePrefetchData(data: IPrefetchData): void {
        this.experimentsDeferred.resolve(data.experiments ? data.experiments : {});
        this.hintsDeferred.resolve(data.carRentalHints ? data.carRentalHints : []);
        this.activeSearchDeferred.resolve(data.activeSearch ? data.activeSearch : {});
        this.flightOriginSuggestion.resolve(data.flightOriginSuggestion || null);
    }

}
